import type {
  ConsoleActor,
  ConsoleTimelineEntry,
} from "@/hooks/queries/admin-console-types";

// Story 15.7 — the client-side derivations behind Screen 10's activity timeline.
//
// The mockup's timeline row is a `<strong>` time-of-day plus a `<span
// class="helper">` SENTENCE, and the sentence shape is "{Who} {did what}." —
// subject first, full stop at the end, no leading timestamp inside the sentence
// itself (the `<strong>` carries that). This file builds those sentences.
//
// WHICH events reach the timeline is the SERVER's allow-list, not this file's.
// What this file owns is how a row READS, so an event name it does not know
// still renders a truthful sentence naming the event rather than nothing — a
// backend that widens its allow-list must not blank a row here first.

/**
 * The three-state actor, mirroring `AuditLogTable.tsx :: ActorCell`:
 *
 *   • a named actor            → their name (or their email, when the projection
 *                                carries no name)
 *   • an actor we cannot name  → "Unknown actor" — someone DID this and the
 *                                projection row is missing
 *   • `actor: null`            → `null` here, and the sentence is built with no
 *                                actor clause at all
 *
 * The middle case and the last one are deliberately different answers. Collapsing
 * them would report a hydration failure as a worker event, and the worker events
 * (contract lapses, renewal reminders) are exactly what an operator comes to this
 * console for.
 */
export function actorLabel(actor: ConsoleActor | null): string | null {
  if (actor === null) return null;
  const name = [actor.first_name, actor.last_name].filter(Boolean).join(" ");
  if (name) return name;
  if (actor.email) return actor.email;
  return "Unknown actor";
}

/**
 * The organization this row is about, or `null`.
 *
 * An id whose name did not resolve renders as the id rather than as nothing:
 * "some organization" would be a claim we cannot support, and the id is at least
 * something an operator can look up. Never fabricated.
 */
function orgLabel(entry: ConsoleTimelineEntry): string | null {
  return entry.organization_name ?? entry.organization_id;
}

/**
 * `changed_fields` as prose. Column NAMES only — 15.6 AC15 keeps the VALUES off
 * this wire deliberately (three of the six editable organization fields are
 * addresses), and AC10 refuses to join back for them.
 */
function changedFieldsClause(changedFields: string[] | null): string {
  if (!changedFields || changedFields.length === 0) return "";
  const readable = changedFields.map((field) => field.replace(/_/g, " "));
  return ` — changed ${readable.join(", ")}`;
}

/**
 * One timeline row as a sentence.
 *
 * The worker-emitted events take the subject position themselves, which is what
 * "render the sentence with no actor clause" means in practice: the row still
 * reads as a statement about something that happened, rather than as a statement
 * about nobody. A literal `System` actor was rejected — it dresses an absence up
 * as a principal — and so was a blank, which is indistinguishable from a
 * hydration failure.
 */
export function timelineSentence(entry: ConsoleTimelineEntry): string {
  const actor = actorLabel(entry.actor);
  const org = orgLabel(entry);
  const orgOr = (fallback: string) => org ?? fallback;

  switch (entry.event_name) {
    // --- Events with a real actor ---
    //
    // Each carries a null arm, for the same reason the worker branches below
    // exist: `actor_user_id` is NULLABLE on the audit row, so an event on this
    // list can arrive without one — a future worker emit under one of these
    // names, or a row written before an actor was recorded. Interpolating the
    // label unguarded renders the literal string "null" as the sentence's
    // subject ("null created Acme Health."), which is worse than either honest
    // answer. The null arm takes the same shape the worker branches use: the
    // subject position goes to the thing the event is about, and no principal
    // is invented. It is NOT the `default:` phrasing — that one names the raw
    // event, which is right for a name this file does not know and wrong for
    // one it does.
    case "organization.created":
      return actor === null
        ? `${orgOr("An organization")} was created.`
        : `${actor} created ${orgOr("an organization")}.`;
    case "organization.updated":
      return actor === null
        ? `${orgOr("An organization")} was updated${changedFieldsClause(
            entry.changed_fields,
          )}.`
        : `${actor} updated ${orgOr("an organization")}${changedFieldsClause(
            entry.changed_fields,
          )}.`;
    case "organization.contract.extended":
      return actor === null
        ? `The contract for ${orgOr("an organization")} was extended.`
        : `${actor} extended the contract for ${orgOr("an organization")}.`;
    // Story 21.1 (FR87). "Renewed", never "extended": the two are different
    // acts on the same record, and the whole reason the timeline carries both
    // names is so a super_admin reading a commercial history can tell a
    // mid-term correction from a term boundary. The sentence names the act and
    // stops — `ConsoleTimelineEntry` carries a fixed field set and 15.6/15.7's
    // posture keeps payload VALUES off this platform-wide read, so the new end
    // date and the new seat count stay on the audit row where the Audit Log
    // renders them.
    case "organization.contract.renewed":
      return actor === null
        ? `The contract for ${orgOr("an organization")} was renewed.`
        : `${actor} renewed the contract for ${orgOr("an organization")}.`;
    case "organization.seat_capacity.adjusted":
      return actor === null
        ? `The seat capacity for ${orgOr("an organization")} was adjusted.`
        : `${actor} adjusted the seat capacity for ${orgOr("an organization")}.`;
    case "org_admin.clinician.invited":
      return actor === null
        ? `A clinician was invited to ${orgOr("their organization")}.`
        : `${actor} invited a clinician to ${orgOr("their organization")}.`;
    // Story 22.4 (FR88). "Attached", never "invited": the arm directly above and
    // this one are two different acts, and carrying both names on the timeline is
    // the only way a reader can tell them apart. An invitation asks someone to
    // join and then waits on them; an attach seats an EXISTING individual
    // clinician outright, on a seat CITS already paid for, and it is the
    // super_admin's own action rather than an org_admin's.
    //
    // The clinician is unnamed on purpose, on the person-scoped pattern the
    // `legacy_cert` arm below uses: `ConsoleTimelineEntry` carries a fixed field
    // set and 15.6/15.7's posture keeps payload VALUES off this platform-wide
    // read, so WHO was seated stays on the audit row the Audit Log renders.
    //
    // Without this arm the event still arrives and falls to `default:`, which
    // prints the raw dotted name — the row appears, but in the machine's words
    // rather than a sentence.
    case "organization.seat.attached":
      return actor === null
        ? `A clinician was attached to ${orgOr("an organization")}.`
        : `${actor} attached a clinician to ${orgOr("an organization")}.`;
    // The correction of the attach above. "Reversed", never "released" or
    // "removed": the org-side departure is a DIFFERENT act under a different
    // name, `org_admin.clinician.removed_from_seat`. That one is not on this
    // allow-list and has no case here, so the two never render side by side —
    // but both are written to the same seven-year trail, which the Audit Log
    // screen reads by raw event name, and this sentence is what a reader carries
    // back to it. Wording that blurred them would undo on the surface the
    // distinction `released_at` exists to make in the data.
    case "organization.seat.attachment_reversed":
      return actor === null
        ? `A clinician's attachment to ${orgOr("an organization")} was reversed.`
        : `${actor} reversed a clinician's attachment to ${orgOr(
            "an organization",
          )}.`;
    case "organization.seat.attachment_reversal_refused":
      return actor === null
        ? `A reversal of a clinician's attachment to ${orgOr(
            "an organization",
          )} was refused.`
        : `${actor} was refused a reversal of a clinician's attachment to ${orgOr(
            "an organization",
          )}.`;
    case "legacy_cert.verification_decided":
      // No organization on this one: it is a decision about a PERSON's legacy
      // AMPS certificate, and naming an org here would invent a subject.
      return actor === null
        ? "A legacy certificate verification was decided."
        : `${actor} decided a legacy certificate verification.`;
    // Story 20.3 (AC15) — the two FR86 calibration-deadline writes, shaped on the
    // person-scoped arm directly above and for the same reason: each concerns ONE
    // clinician, and this payload carries no clinician. The timeline row names
    // WHO ACTED and WHAT ACT it was, and stops there.
    //
    // Naming the target would take an identity off a `super_admin`-wide feed that
    // this query does not fetch and has no business joining — the audited detail
    // lives on the append-only row, which is the surface built for it. That is
    // also why neither sentence quotes the reason: the reason is admin free text
    // written on the understanding that the clinician does not see it, and a
    // platform-wide timeline is the wrong place to start publishing it.
    //
    // Both arms exist for the reason stated at the head of this block — an
    // `actor_user_id` this list cannot assume is present — even though these two
    // events are only ever emitted by a named super_admin today. A null arm that
    // is currently unreachable costs one line; the alternative renders the
    // literal "null" as a sentence's subject the day it stops being unreachable.
    case "admin.calibration_deadline.extended":
      return actor === null
        ? "A calibration deadline was extended."
        : `${actor} extended a calibration deadline.`;
    case "admin.calibration_deadline.reactivated":
      return actor === null
        ? "A calibration was reactivated."
        : `${actor} reactivated a calibration.`;

    // --- Worker-emitted: no actor clause, the subject is the thing itself ---
    case "organization.contract.lapsed":
      return org === null
        ? "An organization contract lapsed."
        : `${org}'s contract lapsed.`;
    case "organization.renewal.reminder_sent":
      return `A renewal reminder was sent for ${orgOr("an organization")}.`;
    case "billing.subscription.checkout_completed":
      return "A subscription checkout completed.";

    // An event the server's allow-list carries and this file does not know yet.
    // Naming it verbatim is the honest fallback: the row stays readable, the
    // operator can see WHAT happened, and nothing is invented about it.
    default:
      return actor === null
        ? `${entry.event_name} was recorded.`
        : `${actor} — ${entry.event_name}.`;
  }
}

/**
 * The `<strong>` timestamp.
 *
 * The mockup prints a bare `09:14`, and every row it shows is from today. This
 * timeline is the most recent 20 events platform-wide, which on a quiet estate
 * reaches back weeks — so a bare time-of-day on a three-week-old row would read
 * as "this morning". Today's rows keep the mockup's exact shape; older rows
 * carry the date as well.
 *
 * Deliberately NOT `formatDateTime`: that helper is the AUDIT console's, where
 * seconds and the zone are load-bearing forensic detail (Story 11.7 AC4). This
 * is a glanceable operations summary and the audit log is one click away.
 *
 * `now` is injectable so the boundary is testable without freezing the clock; it
 * is never passed in production.
 */
export function timelineTimestamp(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "—";

  const timeOfDay = at.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const sameLocalDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  if (sameLocalDay) return timeOfDay;

  // The year appears only when it differs from the current one. Without that
  // arm, a row from a previous year renders identically to the same calendar
  // day this year ("Aug 28, 09:14" for both), and the timeline is unbounded in
  // how far back it will format: the twenty most recent events on a quiet
  // estate can reach back further than the "weeks" the note above assumes.
  // Keeping it conditional preserves the mockup's shape for the rows an
  // operator actually reads day to day.
  const day = at.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(at.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${day}, ${timeOfDay}`;
}
